import { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Container, Form, Row, Spinner } from 'react-bootstrap';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import CountryCodeSelect from '../../components/CountryCodeSelect';
import { useNotificationModal } from '../../hooks/useNotificationModal';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export default function RegisterCompanyAdmins() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isUpdateMode = Boolean(id);
  const companyCodeFromParams = searchParams.get('company_code');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    countryCode: '+94',
    role: 'Company_Admin',
    password: '',
    company_code: companyCodeFromParams || ''
  });

  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);

  const [phoneError, ] = useState('');
  const [passwordRules, setPasswordRules] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    specialChar: false
  });
  const [showRules, setShowRules] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const navigate = useNavigate();
  const { showError, showSuccess } = useNotificationModal();

  // Override console.error to display errors in modal
  useEffect(() => {
    const originalConsoleError = console.error;
    console.error = (...args) => {
      originalConsoleError(...args);
      const errorMessage = args
        .map(arg => (typeof arg === 'object' ? JSON.stringify(arg) : String(arg)))
        .join(' ');
      showError(errorMessage);
    };

    return () => {
      console.error = originalConsoleError;
    };
  }, [showError]);

  // Fetch companies list
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const response = await fetch(`${BASE_URL}/api/admin/companies`);
        const data = await response.json();
        if (data.success) {
          setCompanies(data.companies);
          
          // If company_code is provided in params, auto-select that company
          if (companyCodeFromParams) {
            const matchedCompany = data.companies.find(c => c.company_code === companyCodeFromParams);
            if (matchedCompany) {
              setSelectedCompany(matchedCompany);
              setFormData(prev => ({ ...prev, company_code: companyCodeFromParams }));
            }
          }
        }
      } catch (error) {
        console.error('Error fetching companies:', error);
      }
    };
    
    fetchCompanies();
  }, [companyCodeFromParams, showError]);


  // Fetch admin data for update mode
  const fetchAdminData = useCallback(async () => {
    setIsLoadingData(true);
    setErrorMsg('');
    try {
      const url = `${BASE_URL}/api/admin/auth/get-admin/${id}`;
      
      const response = await fetch(url);
      
      const data = await response.json();

      if (response.ok && data.success) {
        const admin = data.admin;
        
        // Ensure country code is properly formatted with + prefix
        let countryCode = "+94"; // Default
        if (admin.country_code !== null && admin.country_code !== undefined && admin.country_code !== "") {
          const codeStr = String(admin.country_code).trim();
          countryCode = codeStr.startsWith('+') ? codeStr : `+${codeStr}`;
        }
        
        // Use the exact database column names based on backend code
        setFormData({
          name: admin.name || '',
          email: admin.email || '',
          phone: admin.phone_number || '',
          countryCode: countryCode,
          role: admin.role || 'Company_Admin',
          password: '', // Keep empty for security
          company_code: admin.company_code || ''
        });
        
        // Find and set the selected company
        if (admin.company_code) {
          const matchedCompany = companies.find(c => c.company_code === admin.company_code);
          if (matchedCompany) {
            setSelectedCompany(matchedCompany);
          }
        }
      } else {
        console.error('API Error Response:', data);
        setErrorMsg(data.message || 'Failed to fetch admin data');
      }
    } catch (error) {
      console.error('Fetch Error:', error);
      setErrorMsg('Error fetching admin data. Please try again.');
    } finally {
      setIsLoadingData(false);
    }
  }, [id, companies]);

  useEffect(() => {
    if (isUpdateMode && id) {
      fetchAdminData();
    }
  }, [id, isUpdateMode, fetchAdminData]);

  const handleNameChange = (e) => {
    let value = e.target.value.replace(/[^a-zA-Z\s]/g, '');
    setFormData((prev) => ({ ...prev, name: value }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'password') {
      const rules = {
        length: value.length >= 8 && value.length <= 20,
        uppercase: /[A-Z]/.test(value),
        lowercase: /[a-z]/.test(value),
        number: /\d/.test(value),
        specialChar: /[\W_]/.test(value)
      };
      setPasswordRules(rules);
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // const handleCompanySelect = (selectedOption) => {
  //   const company = companies.find(c => c.company_code === selectedOption.value);
  //   setSelectedCompany(company);
  //   setFormData((prev) => ({ ...prev, company_code: selectedOption.value }));
  // };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setSuccessMsg('');
    setErrorMsg('');
  
    // Validate phone number before submitting
    const cleanPhone = formData.phone.replace(/\D/g, '');
    if (cleanPhone.length < 7 || cleanPhone.length > 15) {
      const errorMessage = 'Phone number must be 7-15 digits';
      setErrorMsg(errorMessage);
      showError(errorMessage);
      setIsLoading(false);
      return;
    }

    // Validate country code
    const cleanCountryCode = formData.countryCode.replace(/\D/g, '');
    if (!formData.countryCode.startsWith('+') || cleanCountryCode.length < 1 || cleanCountryCode.length > 4) {
      const errorMessage = 'Country code must start with + and be 1-4 digits';
      setErrorMsg(errorMessage);
      showError(errorMessage);
      setIsLoading(false);
      return;
    }

    // Password validation for update mode (only if password is provided)
    if (isUpdateMode && formData.password) {
      const allRulesPassed = Object.values(passwordRules).every(rule => rule);
      if (!allRulesPassed) {
        const errorMessage = 'Please ensure password meets all requirements';
        setErrorMsg(errorMessage);
        showError(errorMessage);
        setIsLoading(false);
        return;
      }
    }

    // Password validation for create mode
    if (!isUpdateMode) {
      const allRulesPassed = Object.values(passwordRules).every(rule => rule);
      if (!allRulesPassed) {
        const errorMessage = 'Please ensure password meets all requirements';
        setErrorMsg(errorMessage);
        showError(errorMessage);
        setIsLoading(false);
        return;
      }
    }
  
    try {
      if (isUpdateMode) {
        // Update existing admin
        await updateCompanyAdmin();
      } else {
        // Create new admin for existing company
        await createCompanyAdmin();
      }
    } catch (error) {
      console.error(`${isUpdateMode ? 'Update' : 'Registration'} error:`, error);
      const errorMessage = error.message || 'Something went wrong. Please try again.';
      setErrorMsg(errorMessage);
      showError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const createCompanyAdmin = async () => {
    const cleanPhone = formData.phone.replace(/\D/g, '');
    
    if (!formData.company_code) {
      throw new Error('Please select a company');
    }

    // Ensure country code has + prefix before saving
    const countryCodeToSave = formData.countryCode 
      ? (String(formData.countryCode).startsWith('+') 
          ? formData.countryCode 
          : `+${formData.countryCode}`)
      : "+94";

    const adminFormData = new FormData();
    adminFormData.append('name', formData.name);
    adminFormData.append('email', formData.email);
    adminFormData.append('country_code', countryCodeToSave);
    adminFormData.append('phone', cleanPhone);
    adminFormData.append('role', formData.role);
    adminFormData.append('password', formData.password);
    adminFormData.append('company_code', formData.company_code);
    adminFormData.append('frontend_url', window.location.origin);

    const response = await fetch(`${BASE_URL}/api/admin/auth/create-company-admin`, {
      method: 'POST',
      body: adminFormData,
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Failed to create admin user');
    }

    const message = data.message?.toLowerCase().includes('verification')
      ? `A verification email has been sent to ${formData.email}.`
      : `Admin account created successfully for company "${selectedCompany?.company_name}"!`;
    
    setSuccessMsg(message);
    showSuccess(message);
    
    // Clear success message after 5 seconds
    setTimeout(() => setSuccessMsg(''), 5000);

    // Reset form
    setFormData({
      name: '',
      email: '',
      phone: '',
      countryCode: '',
      role: 'Company_Admin',
      password: '',
      company_code: companyCodeFromParams || ''
    });
  };

  const updateCompanyAdmin = async () => {
    const cleanPhone = formData.phone.replace(/\D/g, '');
    
    // Ensure country code has + prefix before saving
    const countryCodeToSave = formData.countryCode 
      ? (String(formData.countryCode).startsWith('+') 
          ? formData.countryCode 
          : `+${formData.countryCode}`)
      : "+94";
    
    const requestBody = {
      name: formData.name,
      email: formData.email,
      country_code: countryCodeToSave,
      phone_number: cleanPhone,
      role: formData.role
    };
    
    // Only include password if provided
    if (formData.password) {
      requestBody.password = formData.password;
    }

    const response = await fetch(`${BASE_URL}/api/admin/auth/update-admin/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody),
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Update failed');
    }

    const message = 'Company admin updated successfully!';
    setSuccessMsg(message);
    showSuccess(message);
    
    // Clear success message after 5 seconds
    setTimeout(() => setSuccessMsg(''), 5000);
    
    // Redirect after a delay
    setTimeout(() => {
      if (formData.company_code) {
        navigate(`/vcm-admin/view-company-admins/${formData.company_code}`);
      } else {
        navigate('/vcm-admin/view-companies');
      }
    }, 2000);
  };

  if (isLoadingData) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className="register-container">
      <Container>
        <Card className="register-card">
          <Button 
            variant="secondary" 
            className="btn-custom-secondary mb-3" 
            onClick={() => {
              if (companyCodeFromParams) {
                navigate(`/vcm-admin/view-company-admins/${companyCodeFromParams}`);
              } else {
                navigate('/vcm-admin/view-companies');
              }
            }}
          >
            ← Back to {companyCodeFromParams ? 'Company admins' : 'Companies'}
          </Button>

          <Card.Body>
            <h2 className="register-title">
              {isUpdateMode ? 'Update Company Admin' : 'Add Company Admin'}
            </h2>

            {successMsg && (
              <div className="mb-3 text-success text-center fw-semibold">
                {successMsg}
              </div>
            )}
            {errorMsg && (
              <Alert variant="danger" className="text-center">
                {errorMsg}
              </Alert>
            )}

            <Form className="register-form" onSubmit={handleSubmit}>
              {selectedCompany && (
                <Row>
                  <Col md={12}>
                    <Card className="mb-3 bg-light">
                      <Card.Body>
                        <h6>Company Details:</h6>
                        <div className="d-flex align-items-center">
                          {selectedCompany.company_logo && (
                            <img 
                              src={`${BASE_URL}/uploads/company_logos/${selectedCompany.company_logo}`} 
                              alt="Company Logo" 
                              style={{
                                width: '50px',
                                height: '50px',
                                objectFit: 'contain',
                                marginRight: '15px',
                                border: '1px solid #dee2e6',
                                borderRadius: '4px',
                                backgroundColor: '#fff',
                                padding: '5px'
                              }}
                            />
                          )}
                          <div>
                            <strong>{selectedCompany.company_name}</strong><br/>
                            <small className="text-muted">Code: {selectedCompany.company_code}</small><br/>
                            <small className="text-muted">Currency: {selectedCompany.currency}</small>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              )}

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Full Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleNameChange}
                      placeholder="Enter full name"
                      required
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Email Address</Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Enter email address"
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Phone Number</Form.Label>
                    <div className="d-flex gap-2">
                      <CountryCodeSelect
                        value={formData.countryCode}
                        onChange={(code) =>
                          setFormData((prev) => ({ ...prev, countryCode: code }))
                        }
                        placeholder="Code"
                        isRequired={true}
                        width="100px"
                      />
                      <Form.Control
                        type="text"
                        name="phone"
                        value={formData.phone}
                        onChange={(e) => {
                          // Only allow digits
                          const value = e.target.value.replace(/\D/g, '');
                          setFormData((prev) => ({ ...prev, phone: value }));
                        }}
                        placeholder="Enter phone number"
                        required
                        style={{ 
                          flex: 1,
                          height: '48px', 
                          fontSize: '1rem', 
                          borderRadius: '12px', 
                          backgroundColor: '#f7fafc', 
                          border: '2px solid #e2e8f0' 
                        }}
                      />
                    </div>
                    <Form.Text className="text-muted">
                      Select country code and enter phone number
                    </Form.Text>
                    {phoneError && (
                      <div className="invalid-feedback d-block">{phoneError}</div>
                    )}
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Password 
                      {isUpdateMode && <small className="text-muted"> (Leave empty to keep current)</small>}
                    </Form.Label>
                    <Form.Control
                      type="password"
                      name="password"  
                      value={formData.password}
                      onChange={handleChange}
                      placeholder={isUpdateMode ? "Enter new password (optional)" : "Enter password"}
                      onFocus={() => setShowRules(true)}
                      onBlur={() => setShowRules(false)}
                      required={!isUpdateMode}
                    />
                    {showRules && (
                      <div className="password-rules mt-2">
                        <small>Password requirements:</small>
                        <ul className="list-unstyled ms-2">
                          <li style={{ color: passwordRules.length ? '#28a745' : '#dc3545' }}>
                            {passwordRules.length ? '✅' : '❌'} 8-20 characters
                          </li>
                          <li style={{ color: passwordRules.uppercase ? '#28a745' : '#dc3545' }}>
                            {passwordRules.uppercase ? '✅' : '❌'} At least one uppercase letter
                          </li>
                          <li style={{ color: passwordRules.lowercase ? '#28a745' : '#dc3545' }}>
                            {passwordRules.lowercase ? '✅' : '❌'} At least one lowercase letter
                          </li>
                          <li style={{ color: passwordRules.number ? '#28a745' : '#dc3545' }}>
                            {passwordRules.number ? '✅' : '❌'} At least one number
                          </li>
                          <li style={{ color: passwordRules.specialChar ? '#28a745' : '#dc3545' }}>
                            {passwordRules.specialChar ? '✅' : '❌'} At least one special character
                          </li>
                        </ul>
                      </div>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              <div className="text-center">
                <Button 
                  type="submit" 
                  className="register-btn"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      {isUpdateMode ? 'Updating...' : 'Registering...'}
                    </>
                  ) : (
                    isUpdateMode ? 'Update Admin' : 'Register Admin'
                  )}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
}
